import { Title } from "@components/ui";
import { Button } from "components";
import gate from "gate";
import Image from "next/image";
import profileImage from "public/images/default-profile.png";
import React, { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { getFormNowDate } from "src/utils";

import { CampaignType } from "../../../../enums/campaign-type";
import style from "../styles.module.css";

const Profile = ({
  fullName,
  createdDate,
  image,
}: {
  fullName: string;
  createdDate: Date;
  image: string;
}) => {
  return (
    <div className="flex items-center">
      <img
        loading="lazy"
        src={
          fullName === "ادمین دادبانان"
            ? "/images/dadbanan-admin.jpg"
            : image
            ? image
            : "/images/default-profile.png"
        }
        className="h-[50px] w-[50px] rounded-full md:h-12 md:w-12"
        width={64}
        height={64}
        alt="profile"
      />
      <div className="mr-3 flex flex-col">
        <span className="text-xs md:text-base">{fullName}</span>
        <small className="text-[10px] text-[#999696] md:text-base">
          {createdDate && getFormNowDate(createdDate)}
        </small>
      </div>
    </div>
  );
};

type Props = {
  comments: [
    {
      description: string;
      fullName: string;
      id: number;
      parentId: number | null;
      title: string;
      userId: number | null;
    }
  ];
  id: number | string | undefined;
  type: "package" | "course" | "student" | "article";
};

type FormValues = {
  description: string;
};

const Comment = ({
  id,
  fullName,
  description,
  image,
  isReply,
  createdDate,
}: any) => {
  return (
    <li
      className={`${style.commentWrapper} pb-7 after:absolute after:mx-auto after:h-px after:w-[90%] after:content-[''] first:py-7 last:after:w-0 md:after:w-[96%]`}
      key={JSON.stringify(id)}
    >
      <div className="flex w-full">
        <div className="w-10/12">
          <Profile {...{ fullName, createdDate, image }} />
        </div>
        {isReply && (
          <div
            key={JSON.stringify(id)}
            className="pointer-events-none flex w-2/12 cursor-default items-center justify-end"
          >
            <Button CClassName="bg-blue-550/20 text-blue-550 text-[10px] md:text-xs py-1 px-3">
              پاسخ
            </Button>
          </div>
        )}
      </div>
      <p className="mt-[10px] mb-7 text-xs md:text-base">{description}</p>
    </li>
  );
};
const CommentUi = ({
  id,
  fullName,
  createdDate,
  image,
  description,
  replies,
}: any) => {
  return (
    <>
      <Comment
        key={JSON.stringify(id)}
        {...{ id, fullName, createdDate, image, description, isReply: false }}
      />

      {replies?.map(({ id, fullName, description }: any) => (
        <Comment
          key={JSON.stringify(id)}
          fullName="ادمین دادبانان"
          {...{ id, description, image, createdDate, isReply: true }}
        />
      ))}
    </>
  );
};

const Comments: FC<Props> = ({ comments, id, type }) => {
  const query = useQueryClient();
  const profileData: any = query?.getQueryData("profile");
  const { mutate, isLoading }: any = useMutation(
    type === "course"
      ? gate.sendComment
      : type === "article"
      ? gate.articleCommentSend
      : gate.packagesCommentSend
  );
  const [isOpenInput, setIsOpenInput] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>();
  const { mutate: createUserActivityMutate } = useMutation(
    gate.createUserActivity
  );
  useEffect(() => {
    if ((comments?.length as any) == 0) {
      setIsOpenInput(true);
    }
  }, [comments]);

  const submit = ({ description }: FormValues) => {
    mutate(
      {
        [type == "course"
          ? "courseId"
          : type === "article"
          ? "postId"
          : "packageId"]: type === "article" ? id : (id as any)[0],
        description,
      },
      {
        onSuccess: () => {
          toast.success(
            "دیدگاه شما با  موفقیت ارسال و پس از تأیید ادمین نمایش داده خواهد شد.",
            { autoClose: 3000 }
          );
          reset();
        },
        onError: (error: any) => {
          if (!error.handleGlobally) {
            toast.error("متاسفانه مشکلی پیش آمد.");
          }
        },
      }
    );
    if (type === "package" || type === "course") {
      let campaign: any = localStorage.getItem("campaign");
      if (campaign && JSON.parse(campaign)) {
        campaign = JSON.parse(campaign);
        createUserActivityMutate({
          campaignId: +campaign.campaign_id,
          followUpId: +campaign.followup_id,
          phoneNumber: campaign.phone_number,
          type: +CampaignType.SendComment,
          url: campaign.url,
          isPromotion: campaign?.isPromotion.toLowerCase() === "true",
        });
      }
    }
  };

  return (
    <div
      className={`h-auto w-full rounded bg-white px-1 text-black-450 shadow-md transition-all md:px-0 `}
      style={{
        contain: "content",
      }}
    >
      <div
        className={`relative rounded transition-all`}
        style={{ contain: "content" }}
      >
        <div
          className={`z-10 flex h-[52px] items-center justify-between px-5 md:h-20 ${
            type == "student" && "sticky top-0"
          }`}
          style={{
            background:
              type !== "student"
                ? "linear-gradient(270deg, #366494 0%, #0480D4 41.97%, #27B2BC 85.02%)"
                : "#e8ebf6",
          }}
        >
          <Title
            type="DOT"
            title="نظرات"
            wrapperClass={
              type !== "student" ? "!text-white mb-0" : "!text-[#1D3FA8]"
            }
            dotClass={type !== "student" ? "!bg-white" : "!bg-[#1D3FA8]"}
          />
          {type !== "student" && (
            <Button
              disabled={isOpenInput}
              CClassName="rounded bg-blue-550 py-2 px-3 text-white"
              onClick={() => setIsOpenInput(true)}
            >
              <div className="ml-2 flex items-center text-xl">+</div>
              افزودن دیدگاه
            </Button>
          )}
        </div>
        <ul className="bg-white px-5">
          {isOpenInput && (
            <form
              onSubmit={handleSubmit(submit)}
              className="flex animate-fade-in-down flex-col pb-5"
            >
              <div className="mt-[30px] mb-5 flex items-center">
                <Image
                  src={profileImage}
                  className="h-16 w-16 rounded-full"
                  alt="کاربر"
                />
                <span className="mr-5">
                  {" "}
                  {profileData?.data?.peopleInfo?.fullName ?? "کاربر مهمان"}
                </span>
              </div>
              <textarea
                {...register("description", {
                  required: "این فیلد نمیتواند خالی باشد",
                })}
                className={`${
                  errors.description?.message && "placeholder:text-red-500"
                } mt-5 h-32 w-full rounded border border-[#B8B7B7] p-5 outline-0`}
                placeholder="متن مورد نظر خود را وارد نمایید. "
              />
              <div className="mt-5 flex justify-end">
                <Button
                  type="button"
                  onClick={() => setIsOpenInput(false)}
                  CClassName="border ml-2 border-[#B8B7B7] px-3 py-2 text-[#B8B7B7]"
                >
                  انصراف
                </Button>
                <Button
                  isLoading={isLoading}
                  type="submit"
                  CClassName="bg-blue-550 px-12 py-2 text-white"
                >
                  ثبت
                </Button>
              </div>
            </form>
          )}
          {comments?.map(
            ({
              description,
              fullName,
              createdDate,
              id,
              parentId,
              title,
              userId,
              image,
              replies,
            }: any) => (
              <CommentUi
                key={JSON.stringify(id)}
                {...{ description, id, image, createdDate, fullName, replies }}
              />
            )
          )}
        </ul>
      </div>
    </div>
  );
};

export default Comments;
